import { useCallback, useContext, useRef } from "react";
import { AppContext, Layer, Polygon } from "../../../context/context";
import { useEditLayer } from "../../layer_panel/functions/layerFunctions";
import { Polygon as LeafletPolygon, CircleMarker } from "react-leaflet";
import { v4 as uuid4 } from "uuid";
import L from "leaflet";
import { LatLngTuple } from "leaflet";
import { logEvent } from '../../../services/analytics';

const HIGH_CONTRAST_POLYGON_BORDER = 'rgba(8, 40, 110, 0.95)';
const HIGH_CONTRAST_POLYGON_FILL = 'rgba(13, 71, 161, 0.36)';
const MIN_POLYGON_BORDER_WIDTH = 3.5;
const MIN_POLYGON_FILL_OPACITY = 0.36;

// Function to add a completed polygon to the active layer
export function useAddPolygon() {
    const context = useContext(AppContext);
    const editLayer = useEditLayer();

    if (!context) {
        throw new Error("useAddPolygon must be used within the context provider")
    }

    const { global } = context;
    const { activeLayerId, mapLayers } = global;

    return (coordinates: [number, number][]): string | undefined => {
        // Check if there are any layers
        if (mapLayers.length === 0) {
            console.error('No layers available for polygon creation. Please create a layer first.');
            return undefined;
        }

        // Use active layer or default to the last layer
        let newActiveLayerId: number;
        if (activeLayerId == null || mapLayers.find(l => l.layerId === activeLayerId) == null) {
            // No valid active layer, use the first layer
            newActiveLayerId = mapLayers[0].layerId;
            global.setActiveLayerId(mapLayers[0].layerId);
        } else {
            newActiveLayerId = activeLayerId as number;
        }

        // Create the new polygon
        const newPolygon: Polygon = {
            id: uuid4(),
            layerId: newActiveLayerId,
            type: 'polygon',
            name: 'New Polygon',
            description: '',
            colour: HIGH_CONTRAST_POLYGON_FILL,
            opacity: '1',
            borderColour: HIGH_CONTRAST_POLYGON_BORDER,
            borderWidth: MIN_POLYGON_BORDER_WIDTH,
            isLocked: false,
            coordinates: [coordinates], // Polygon format expects array of arrays of coordinates
            createdAt: Date.now()
        };

        // --- Analytics: Log polygon creation ---
        logEvent({
            category: 'Shape',
            action: 'CreatePolygon',
            label: 'Polygon Created',
            vertexCount: coordinates.length,
            coordinates: coordinates
        });

        // Update the layer with the new polygon
        const layer: Layer | undefined = mapLayers.find(l => l.layerId === newActiveLayerId);
        if (!layer) {
            console.error(`Layer with ID ${newActiveLayerId} not found for polygon creation. Available layers:`, mapLayers.map(l => l.layerId));
            return undefined;
        }
        
        const newShapes = [...(layer.shapes || []), newPolygon];
        const newLayer: Layer = {
            ...layer,
            shapes: newShapes
        };

        editLayer(newLayer, newActiveLayerId);
        return newPolygon.id;
    };
}

// Function to handle polygon movement
export function usePolygonMovement() {
    const context = useContext(AppContext);

    if (!context) {
        throw new Error("usePolygonMovement must be used within the context provider");
    }

    const { global } = context;
    const { mapLayers, setSelectedShapes } = global;

    const movePolygon = (polygonId: string, newCoordinates: [number, number][][], maintainSelection: boolean = true) => {
        const updatedLayers = mapLayers.map(layer => ({
            ...layer,
            shapes: (layer.shapes || []).map(shape => {
                if (shape.type === 'polygon' && shape.id === polygonId) {
                    return {
                        ...shape,
                        coordinates: newCoordinates
                    };
                }
                return shape;
            })
        }));

        global.setLayers(updatedLayers);
        if (maintainSelection) {
            setSelectedShapes([polygonId]);
        }
    };

    return movePolygon;
}

// Function to render all polygons on the map
export function useRenderPolygons() {
    const context = useContext(AppContext);
    const movePolygon = usePolygonMovement();
    const polygonLayerRefs = useRef<Map<string, L.Polygon>>(new Map());

    if (!context) {
        throw new Error("useRenderPolygons must be used within the AppContext provider");
    }

    const { global, setModeSettings } = context;
    const { mapLayers, setSelectedShapes } = global;
    const setPolygonLayerRef = useCallback((polygonId: string, layer: L.Polygon | null) => {
        if (!layer) {
            polygonLayerRefs.current.delete(polygonId);
            return;
        }
        polygonLayerRefs.current.set(polygonId, layer);
    }, []);

    return () => {
        // Reverse layers array so top layer in menu renders on top visually
        return mapLayers
            .filter(layer => layer && !layer.hidden)
            .slice()
            .reverse()
            .flatMap((layer) =>
                // Reverse shapes array so top shape in menu renders on top visually
                (layer.shapes || []).slice().reverse().map((shape) => {
                    if (shape.type === 'polygon' && !shape.hidden) {
                        const polygon = shape as Polygon;
                        if (!polygon.coordinates[0] || polygon.coordinates[0].length < 3) return null;
                        
                        // Handle polygons with holes - Leaflet expects array of arrays where first is exterior, rest are holes
                        const positions: LatLngTuple[][] = polygon.coordinates.map(ring => 
                            ring.map(coord => [coord[0], coord[1]] as LatLngTuple)
                        );

                        const isSelected = global.selectedShapes.includes(polygon.id);

                        // Calculate midpoints between vertices for potential new vertex positions (only for exterior ring)
                        const exteriorPositions = positions[0];
                        const midPoints: LatLngTuple[] = exteriorPositions.map((pos, idx) => {
                            const nextPos = exteriorPositions[(idx + 1) % exteriorPositions.length];
                            return [
                                (pos[0] + nextPos[0]) / 2,
                                (pos[1] + nextPos[1]) / 2
                            ] as LatLngTuple;
                        });

                        return (
                            <>
                                <LeafletPolygon
                                    ref={(layer) => setPolygonLayerRef(polygon.id, layer)}
                                    key={`polygon-${polygon.id}-${polygon.colour}-${polygon.borderColour}-${polygon.opacity}-${polygon.borderWidth}`}
                                    positions={positions}
                                    pathOptions={{
                                        color: polygon.borderColour || HIGH_CONTRAST_POLYGON_BORDER,
                                        weight: Math.max(polygon.borderWidth ?? 0, MIN_POLYGON_BORDER_WIDTH),
                                        fillColor: polygon.colour || HIGH_CONTRAST_POLYGON_FILL,
                                        fillOpacity: Math.max(parseFloat(polygon.opacity), MIN_POLYGON_FILL_OPACITY),
                                        dashArray: isSelected ? "5, 5" : undefined
                                    }}
                                    eventHandlers={{
                                        click: (e) => {
                                            if (context.modeSettings.mode === 'Select') {
                                                e.originalEvent.preventDefault();
                                                if (e.originalEvent.ctrlKey) {
                                                    // Toggle selection with Ctrl+click
                                                    setSelectedShapes(prev => 
                                                        prev.includes(polygon.id)
                                                            ? prev.filter(id => id !== polygon.id)
                                                            : [...prev, polygon.id]
                                                    );
                                                } else {
                                                    // Single selection without Ctrl
                                                    setSelectedShapes([polygon.id]);
                                                    setModeSettings(prev => ({
                                                        ...prev,
                                                        editingPolygonId: polygon.id,
                                                        editingVertexIndex: null
                                                    }));
                                                }
                                            }
                                        },
                                        dblclick: (e) => {
                                            e.originalEvent.preventDefault();
                                            L.DomEvent.stop(e.originalEvent);
                                            setSelectedShapes([polygon.id]);
                                            setModeSettings(prev => ({
                                                ...prev,
                                                mode: 'Select',
                                                editingPolygonId: polygon.id,
                                                editingVertexIndex: null,
                                                drawingPolygonPoints: [],
                                                drawingLinePoints: []
                                            }));
                                        },
                                        contextmenu: (e) => {
                                            e.originalEvent.preventDefault();
                                            const customEvent = new CustomEvent('polygonContextMenu', {
                                                detail: {
                                                    x: e.originalEvent.clientX,
                                                    y: e.originalEvent.clientY,
                                                    polygon: polygon
                                                }
                                            });
                                            window.dispatchEvent(customEvent);
                                        }
                                    }}
                                />
                                
                                {isSelected && !polygon.isLocked && (
                                    <>
                                        {/* Render existing vertices (only for exterior ring) */}
                                        {exteriorPositions.map((pos, idx) => (
                                            <CircleMarker
                                                key={`vertex-${polygon.id}-${idx}`}
                                                center={pos}
                                                radius={14}
                                                bubblingMouseEvents={false}
                                                eventHandlers={{
                                                    mouseover: (e) => {
                                                        const el = e.target;
                                                        el.setStyle({ fillOpacity: 0.8, weight: 4 });
                                                    },
                                                    mouseout: (e) => {
                                                        const el = e.target;
                                                        el.setStyle({ fillOpacity: 1, weight: 3 });
                                                    },
                                                    contextmenu: (e) => {
                                                        e.originalEvent.preventDefault();
                                                        if (polygon.coordinates[0].length > 3) {
                                                            const newCoords = [...polygon.coordinates[0]];
                                                            newCoords.splice(idx, 1);
                                                            
                                                            // --- Analytics: Log vertex removal ---
                                                            logEvent({
                                                                category: 'Shape',
                                                                action: 'VertexRemoved',
                                                                label: 'Polygon Vertex Removed',
                                                                shapeId: polygon.id,
                                                                shapeType: 'polygon',
                                                                oldVertexCount: polygon.coordinates[0].length,
                                                                newVertexCount: newCoords.length,
                                                                vertexIndex: idx
                                                            });
                                                            
                                                            movePolygon(polygon.id, [newCoords], true);
                                                        }
                                                    },
                                                    mousedown: (e) => {
                                                        L.DomEvent.stop(e.originalEvent);
                                                        const map = e.target._map;
                                                        map.dragging.disable();
                                                        let latestCoords = [...polygon.coordinates[0]];
                                                        let frameId: number | null = null;
                                                        let didMove = false;
                                                        const polygonLayer = polygonLayerRefs.current.get(polygon.id);

                                                        const renderLivePolygon = () => {
                                                            if (!polygonLayer) return;
                                                            const livePositions = latestCoords.map(
                                                                (coord) => [coord[0], coord[1]] as LatLngTuple
                                                            );
                                                            polygonLayer.setLatLngs([livePositions]);
                                                        };

                                                        const scheduleMove = () => {
                                                            if (frameId !== null) return;
                                                            frameId = window.requestAnimationFrame(() => {
                                                                frameId = null;
                                                                renderLivePolygon();
                                                            });
                                                        };
                                                        
                                                        const handleVertexMove = (moveEvent: any) => {
                                                            if (moveEvent.originalEvent) {
                                                                L.DomEvent.stop(moveEvent.originalEvent);
                                                            }
                                                            const nextLatLng: [number, number] = [moveEvent.latlng.lat, moveEvent.latlng.lng];
                                                            latestCoords[idx] = nextLatLng;
                                                            e.target.setLatLng(nextLatLng);
                                                            didMove = true;
                                                            scheduleMove();
                                                        };
                                                        
                                                        const handleVertexUp = (upEvent: any) => {
                                                            if (upEvent.originalEvent) {
                                                                L.DomEvent.stop(upEvent.originalEvent);
                                                            }
                                                            if (frameId !== null) {
                                                                window.cancelAnimationFrame(frameId);
                                                                frameId = null;
                                                            }
                                                            if (didMove) {
                                                                movePolygon(polygon.id, [latestCoords], true);
                                                            }
                                                            map.dragging.enable();
                                                            map.off('mousemove', handleVertexMove);
                                                            map.off('mouseup', handleVertexUp);
                                                            setTimeout(() => setSelectedShapes([polygon.id]), 0);
                                                        };

                                                        map.on('mousemove', handleVertexMove);
                                                        map.on('mouseup', handleVertexUp);
                                                    }
                                                }}
                                                pathOptions={{
                                                    color: '#1a237e',
                                                    weight: 5,
                                                    fillColor: '#e8edff',
                                                    fillOpacity: 1
                                                }}
                                            />
                                        ))}
                                        
                                        {/* Render midpoints for adding new vertices */}
                                        {midPoints.map((pos, idx) => (
                                            <CircleMarker
                                                key={`midpoint-${polygon.id}-${idx}`}
                                                center={pos}
                                                radius={9}
                                                bubblingMouseEvents={false}
                                                eventHandlers={{
                                                    mouseover: (e) => {
                                                        const el = e.target;
                                                        el.setStyle({ fillOpacity: 0.8, weight: 4 });
                                                    },
                                                    mouseout: (e) => {
                                                        const el = e.target;
                                                        el.setStyle({ fillOpacity: 1, weight: 3 });
                                                    },
                                                    mousedown: (e) => {
                                                        L.DomEvent.stop(e.originalEvent);
                                                        
                                                        const newCoords = [...polygon.coordinates[0]];
                                                        newCoords.splice(idx + 1, 0, [pos[0], pos[1]]);
                                                        
                                                        // --- Analytics: Log vertex addition ---
                                                        logEvent({
                                                            category: 'Shape',
                                                            action: 'VertexAdded',
                                                            label: 'Polygon Vertex Added',
                                                            shapeId: polygon.id,
                                                            shapeType: 'polygon',
                                                            oldVertexCount: polygon.coordinates[0].length,
                                                            newVertexCount: newCoords.length,
                                                            vertexIndex: idx + 1
                                                        });
                                                        
                                                        movePolygon(polygon.id, [newCoords], true);
                                                        
                                                        const map = e.target._map;
                                                        map.dragging.disable();
                                                        let latestCoords = [...newCoords];
                                                        let frameId: number | null = null;
                                                        let didMove = false;
                                                        const polygonLayer = polygonLayerRefs.current.get(polygon.id);

                                                        const renderLivePolygon = () => {
                                                            if (!polygonLayer) return;
                                                            const livePositions = latestCoords.map(
                                                                (coord) => [coord[0], coord[1]] as LatLngTuple
                                                            );
                                                            polygonLayer.setLatLngs([livePositions]);
                                                        };

                                                        const scheduleMove = () => {
                                                            if (frameId !== null) return;
                                                            frameId = window.requestAnimationFrame(() => {
                                                                frameId = null;
                                                                renderLivePolygon();
                                                            });
                                                        };
                                                        
                                                        const handleNewVertexMove = (moveEvent: any) => {
                                                            if (moveEvent.originalEvent) {
                                                                L.DomEvent.stop(moveEvent.originalEvent);
                                                            }
                                                            const nextLatLng: [number, number] = [moveEvent.latlng.lat, moveEvent.latlng.lng];
                                                            latestCoords[idx + 1] = nextLatLng;
                                                            e.target.setLatLng(nextLatLng);
                                                            didMove = true;
                                                            scheduleMove();
                                                        };
                                                        
                                                        const handleNewVertexUp = (upEvent: any) => {
                                                            if (upEvent.originalEvent) {
                                                                L.DomEvent.stop(upEvent.originalEvent);
                                                            }
                                                            if (frameId !== null) {
                                                                window.cancelAnimationFrame(frameId);
                                                                frameId = null;
                                                            }
                                                            if (didMove) {
                                                                movePolygon(polygon.id, [latestCoords], true);
                                                            }
                                                            map.dragging.enable();
                                                            map.off('mousemove', handleNewVertexMove);
                                                            map.off('mouseup', handleNewVertexUp);
                                                            setTimeout(() => setSelectedShapes([polygon.id]), 0);
                                                        };

                                                        map.on('mousemove', handleNewVertexMove);
                                                        map.on('mouseup', handleNewVertexUp);
                                                    }
                                                }}
                                                pathOptions={{
                                                    color: '#0d47a1',
                                                    weight: 4,
                                                    fillColor: '#f3f6ff',
                                                    fillOpacity: 1,
                                                    dashArray: "3, 3"
                                                }}
                                            />
                                        ))}
                                    </>
                                )}
                            </>
                        );
                    }
                    return null;
                })
            );
    };
}

// Function to handle the temporary polygon being drawn
export function useRenderTempPolygon(onPolygonCompleted?: () => void) {
    const context = useContext(AppContext);
    const tempPolygonLayerRef = useRef<L.Polygon | null>(null);
    const addPolygon = useAddPolygon();

    if (!context) {
        throw new Error("useRenderTempPolygon must be used within the AppContext provider");
    }

    const { modeSettings, setModeSettings } = context;
    const { drawingPolygonPoints } = modeSettings;
    return () => {
        // Render even if we only have a single point
        if (drawingPolygonPoints.length > 0) {
            // Convert to the format Leaflet expects with proper typing
            const positions: LatLngTuple[] = drawingPolygonPoints.map(coord => 
                [coord[0], coord[1]] as LatLngTuple
            );
            
            // Only render the polygon line if we have at least 2 points
            const polygonElement = drawingPolygonPoints.length > 1 ? (
                <LeafletPolygon
                    ref={(layer) => {
                        tempPolygonLayerRef.current = layer;
                    }}
                    key="temp-polygon"
                    positions={positions}
                    pathOptions={{
                        color: HIGH_CONTRAST_POLYGON_BORDER,
                        weight: 4.5,
                        fillColor: HIGH_CONTRAST_POLYGON_FILL,
                        fillOpacity: 0.55,
                        dashArray: "5, 5" // Dashed line to indicate it's temporary
                    }}
                />
            ) : null;

            return (
                <>
                    {polygonElement}
                    
                    {/* Render circles for all vertices */}
                    {positions.map((position, index) => (
                        <CircleMarker
                            key={`vertex-${index}`}
                            center={position}
                            radius={index === 0 ? 16 : 11} // First vertex is larger
                            bubblingMouseEvents={false}
                            eventHandlers={{
                                click: (e) => {
                                    L.DomEvent.stop(e.originalEvent);
                                    const isFirstVertex = index === 0;
                                    const isLatestVertex = index === drawingPolygonPoints.length - 1;
                                    if (
                                        (isFirstVertex || isLatestVertex) &&
                                        drawingPolygonPoints.length >= 3 &&
                                        modeSettings.mode === 'Draw Polygon'
                                    ) {
                                        addPolygon(drawingPolygonPoints);
                                        setModeSettings(prev => ({
                                            ...prev,
                                            drawingPolygonPoints: []
                                        }));
                                        onPolygonCompleted?.();
                                    }
                                },
                                dblclick: (e) => {
                                    L.DomEvent.stop(e.originalEvent);
                                },
                                mouseup: (e) => {
                                    L.DomEvent.stop(e.originalEvent);
                                },
                                mousedown: (e) => {
                                    L.DomEvent.stop(e.originalEvent);
                                    const map = e.target._map;
                                    map.dragging.disable();
                                    let latestPoint: [number, number] = [position[0], position[1]];
                                    let latestPoints = [...modeSettings.drawingPolygonPoints];
                                    let frameId: number | null = null;
                                    let didMove = false;

                                    const renderLiveDraftPolygon = () => {
                                        if (!tempPolygonLayerRef.current) return;
                                        if (latestPoints.length < 2) return;
                                        const livePositions = latestPoints.map(
                                            (coord) => [coord[0], coord[1]] as LatLngTuple
                                        );
                                        tempPolygonLayerRef.current.setLatLngs(livePositions);
                                    };

                                    const scheduleMove = () => {
                                        if (frameId !== null) return;
                                        frameId = window.requestAnimationFrame(() => {
                                            frameId = null;
                                            renderLiveDraftPolygon();
                                        });
                                    };

                                    const handleDraftVertexMove = (moveEvent: any) => {
                                        if (moveEvent.originalEvent) {
                                            L.DomEvent.stop(moveEvent.originalEvent);
                                        }
                                        latestPoint = [moveEvent.latlng.lat, moveEvent.latlng.lng];
                                        latestPoints[index] = latestPoint;
                                        e.target.setLatLng(latestPoint);
                                        didMove = true;
                                        scheduleMove();
                                    };

                                    const handleDraftVertexUp = (upEvent: any) => {
                                        if (upEvent.originalEvent) {
                                            L.DomEvent.stop(upEvent.originalEvent);
                                        }
                                        if (frameId !== null) {
                                            window.cancelAnimationFrame(frameId);
                                            frameId = null;
                                        }
                                        if (didMove) {
                                            setModeSettings(prev => {
                                                const nextPoints = [...prev.drawingPolygonPoints];
                                                if (!nextPoints[index]) return prev;
                                                nextPoints[index] = latestPoint;
                                                return {
                                                    ...prev,
                                                    drawingPolygonPoints: nextPoints
                                                };
                                            });

                                            // Swallow the synthetic click that can fire after drag-end so
                                            // Draw Polygon mode doesn't interpret it as "add a new vertex".
                                            map.once('click', (clickEvent: any) => {
                                                if (clickEvent?.originalEvent) {
                                                    L.DomEvent.stop(clickEvent.originalEvent);
                                                }
                                            });
                                        }
                                        map.dragging.enable();
                                        map.off('mousemove', handleDraftVertexMove);
                                        map.off('mouseup', handleDraftVertexUp);
                                    };

                                    map.on('mousemove', handleDraftVertexMove);
                                    map.on('mouseup', handleDraftVertexUp);
                                }
                            }}
                            pathOptions={{
                                color: index === 0 ? '#0b1f63' : HIGH_CONTRAST_POLYGON_BORDER,
                                weight: 4,
                                fillColor: '#f2f6ff',
                                fillOpacity: 1
                            }}
                        />
                    ))}
                </>
            );
        }
        return null;
    };
} 
